import {
  SpeedCategory,
  useIsTimelineExpanded,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import { useMachemetryTicks } from "components/DataGetter/DataDumpRowsGetterForSingleTimeline";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useSpeedGainLossTimelineSegments } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useEffect, memo, useState, useMemo } from "react";
import {
  BREAKPOINT_DESKTOP_XL,
  getIsSafari,
  useBreakpoints,
} from "constants/constants";
import styled from "styled-components/macro";
import { Paper } from "@mui/material";
import { producingColor } from "constants/colors";
import { speedGainDarker, speedLoss } from "reports/constants/colors";
import { safariOffset } from "./safariOffset";
import { clamp } from "lodash";
import {
  TAGHIGHLIGHT_BACKDROP_HEIGHT,
  TAGHIGHLIGHT_BACKDROP_MARGIN_TOP,
} from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";

const isSafari = getIsSafari();
const isMacOs = getIsMacOs();
function getIsMacOs() {
  return (
    window.navigator?.platform?.includes("Mac") ??
    ((window.navigator as any).userAgentData?.platform &&
      (window.navigator as any).userAgentData?.platform === "macOS")
  );
}
const macOffset = isMacOs ? -safariOffset : 0; // ???

const getIsWindows = () => {
  return (
    window.navigator?.platform?.includes("Win") ??
    ((window.navigator as any).userAgentData?.platform &&
      (window.navigator as any).userAgentData?.platform === "Windows")
  );
};
const isWindows = getIsWindows();
const windowsOffset =
  isWindows && !isSafari && window.innerWidth < BREAKPOINT_DESKTOP_XL
    ? -safariOffset + 2
    : 0;

/** this tooltip tracks where the mouse position is and infers which machemetryTick we're moused over, and displays in a floating tooltip the human-readable timestamp of the tick, plus time-since-last-tick and time-to-next-tick */
export const TicksTooltip = ({ order }: { order: number }) => {
  const [isTimelineExpanded] = useIsTimelineExpanded();

  // Track if tooltip is currently visible
  return isTimelineExpanded ? <TicksTooltipContent order={order} /> : null;
};

interface TicksTooltipContentProps {
  order: number;
}
export const TicksTooltipContent = memo(
  ({ order }: TicksTooltipContentProps) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const { scaleTime, xLeft, xRight } = useTimeAxisContext();
    const [machemetryTicks] = useMachemetryTicks();
    const { isDesktopXlOrLarger } = useBreakpoints();
    const processId = useProcessIdOnSingleProcessPage();
    const speedGainLossSegments = useSpeedGainLossTimelineSegments();

    // whenever scaleTime changes, reset tooltip visibility
    useEffect(() => {
      setIsTooltipVisible(false);
    }, [scaleTime]);

    // Pre-compute latest ticks for each tick type
    // This optimization ensures we don't have to find the latest tick on every mouse movement
    const latestTicksInfo = useMemo(() => {
      const tickTypes = [
        "grossCount_0",
        "netCount_0",
        "rejectCount_0",
        "speedGainLoss",
      ];
      const result: Record<
        string,
        { latestTick: any; latestTs: number; latestTickX: number }
      > = {};

      // Use manual for loop for better performance on tick types
      for (let t = 0; t < tickTypes.length; t++) {
        const tickType = tickTypes[t];
        const ticks = machemetryTicks[tickType] || [];

        // Initialize to null/0 to avoid extra check later
        let latestTick = null;
        let latestTs = 0;
        let foundAnyVisibleTicks = false;

        // Combine filtering and finding latest tick in a single pass
        for (let i = 0; i < ticks.length; i++) {
          const tick = ticks[i];
          if (!tick) {
            continue;
          }

          const tickTs = tick?.ts ?? 0;
          const tickX = scaleTime(tickTs * 1000);

          // Check if tick is visible in current range
          if (tickX >= xLeft && tickX <= xRight) {
            // If it's the first visible tick or a later one, update
            if (!foundAnyVisibleTicks || tickTs > latestTs) {
              latestTs = tickTs;
              latestTick = tick;
            }
            foundAnyVisibleTicks = true;
          }
        }

        const latestTickX = latestTick ? scaleTime(latestTs * 1000) : 0;

        result[tickType] = { latestTick, latestTs, latestTickX };
      }

      return result;
    }, [machemetryTicks, scaleTime, xLeft, xRight]); // Recompute when ticks, scaleTime or visible range changes

    // for each canvas, add the mousemove handlers which render the tooltip
    useEffect(() => {
      const canvases = document.querySelectorAll<HTMLCanvasElement>(
        "canvas.ticks-canvas"
      );
      if (!canvases.length) {
        return;
      }
      const handleMouseMove = (e: MouseEvent, canvas: HTMLCanvasElement) => {
        // When mouse is moving over ticks, tooltip is visible
        setIsTooltipVisible(true);
        const tickType = canvas.getAttribute("data-tick-type") as
          | "grossCount_0"
          | "netCount_0"
          | "rejectCount_0"
          | "speedGainLoss";
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const isGross = machemetryTicks.grossCount_0.length > 0;
        const grossOrNetTicks = isGross ? "grossCount_0" : "netCount_0";
        // if we're looking at speedGainLoss, we need to use the associated count ticks
        const tickTypeToUse =
          tickType === "speedGainLoss" ? grossOrNetTicks : tickType;
        const ticks = machemetryTicks[tickTypeToUse];

        // Use already filtered visible ticks from pre-computed data
        const visibleTicks = (ticks || []).filter(tick => {
          const tickX = scaleTime((tick?.ts ?? 0) * 1000);
          return tickX >= xLeft && tickX <= xRight;
        });

        // Get pre-computed latest tick info for the current tick type
        const { latestTickX } = latestTicksInfo[tickTypeToUse] || {
          latestTickX: 0,
        };

        // Check if mouse is beyond the latest tick (in the future)
        // We add a small buffer to prevent immediate switching when just slightly past the latest tick
        const isMouseInFuture = latestTickX > 0 && x > latestTickX + 10;
        const { tick: closestTick, tickX } = visibleTicks?.reduce(
          (accumulator, tick) => {
            const tickX = scaleTime((tick?.ts ?? 0) * 1000);
            const closestX = scaleTime((accumulator.tick?.ts ?? 0) * 1000);
            if (tickType !== "speedGainLoss") {
              const xDiff = Math.abs(tickX - x);
              const closestDiff = Math.abs(closestX - x);
              return xDiff < closestDiff ? { tick, tickX } : accumulator;
            }
            // in the case of speedGainLoss timeline, always return leftmost tick so we can center on the segment between the ticks
            const isLeftSideTick = tickX < x;
            const isLeftSideCurrentClosest = accumulator.tickX < x;
            const isLeftSideCurrentClosestCloserThanTick =
              Math.abs(closestX - x) < Math.abs(tickX - x);
            const isTickToLeft =
              isLeftSideCurrentClosest &&
              isLeftSideTick &&
              !isLeftSideCurrentClosestCloserThanTick;
            return isTickToLeft ? { tick, tickX } : accumulator;
          },
          {
            tick: machemetryTicks[tickType]?.[0],
            tickX: scaleTime((machemetryTicks[tickType]?.[0]?.ts ?? 0) * 1000),
          }
        ) ?? {
          tick: null,
          tickX: 0,
        };

        const tickOrder = TICKS_ORDER[tickType] + order;

        const tooltip = document.getElementById("ticks-tooltip");
        const desktopXlOrLargerOffset = isDesktopXlOrLarger ? 32 : 0;

        if (tooltip) {
          // Default timestamp from closest tick (for non-speed tooltips)
          const localeString = closestTick
            ? new Date(closestTick?.ts * 1000).toLocaleString()
            : "--";

          // Extract just the time portion for display
          let time = localeString.split(",")[1] || "--";

          // Variable to store our tooltip position - default to tickX
          let tooltipPositionX = tickX;

          if (tickType === "speedGainLoss" && processId && !isMouseInFuture) {
            // For speedGainLoss, find the current segment the mouse is over
            const currentDate = scaleTime.invert
              ? scaleTime.invert(x)
              : new Date(0);
            const currentTimestamp =
              currentDate instanceof Date ? currentDate.getTime() : 0;

            // Get process segments if they exist
            const processSegments = speedGainLossSegments[processId] || [];

            // Find the nearest segment to the current time point
            let foundSegment: {
              speedPercentage?: number;
              targetRate?: number;
              category?: (typeof SpeedCategory)[keyof typeof SpeedCategory];
              range?: { ge: number; lt: number };
            } | null = null;
            // for calculating time between segments, also find the next segment
            let nextFoundSegment: {
              speedPercentage?: number;
              targetRate?: number;
              category?: (typeof SpeedCategory)[keyof typeof SpeedCategory];
              range?: { ge: number; lt: number };
            } | null = null;

            // First try to find a segment that contains the current timestamp
            for (const segment of processSegments) {
              // Make sure we're dealing with valid data
              if (
                segment &&
                segment.range &&
                typeof segment.range.ge === "number" &&
                typeof segment.range.lt === "number" &&
                currentTimestamp >= segment.range.ge &&
                currentTimestamp <= segment.range.lt
              ) {
                foundSegment = segment;
                nextFoundSegment =
                  processSegments[processSegments.indexOf(segment) + 1] || null;
                break;
              }
            }

            // If no segment contains the timestamp, find the nearest segment
            if (!foundSegment && processSegments.length > 0) {
              let closestSegment = processSegments[0];
              let smallestDistance = Number.MAX_VALUE;

              for (const segment of processSegments) {
                if (
                  segment &&
                  segment.range &&
                  typeof segment.range.ge === "number" &&
                  typeof segment.range.lt === "number"
                ) {
                  // Calculate distance to segment center
                  const segmentCenter =
                    (segment.range.ge + segment.range.lt) / 2;
                  const distance = Math.abs(currentTimestamp - segmentCenter);

                  if (distance < smallestDistance) {
                    smallestDistance = distance;
                    closestSegment = segment;
                  }
                }
              }

              // Set the closest segment as the found segment
              foundSegment = closestSegment;

              // Find the next segment (if any)
              const segmentIndex = processSegments.indexOf(closestSegment);
              nextFoundSegment = processSegments[segmentIndex + 1] || null;
            }

            // Calculate the proper tooltip position based on the segment
            if (foundSegment && foundSegment.range) {
              const segmentStart = foundSegment.range.ge;
              const segmentEnd = foundSegment.range.lt || Date.now();
              const segmentCenterTime = (segmentStart + segmentEnd) / 2;

              // Position tooltip over the segment center
              tooltipPositionX = scaleTime(new Date(segmentCenterTime));

              // Use the segment's timestamp instead of mouse position timestamp
              const segmentTimestamp = new Date(
                segmentCenterTime
              ).toLocaleString();
              time = segmentTimestamp.split(",")[1] || time;
            }

            if (foundSegment) {
              // We found a segment at the current mouse position
              const speedPercent = foundSegment.speedPercentage || 0;
              const targetRate = foundSegment.targetRate || 0;
              const category = foundSegment.category;

              let speedStatus;
              let statusColor;

              // Determine status based on the category or speed percentage
              if (
                category === SpeedCategory.AT ||
                Math.abs(speedPercent) <= 10
              ) {
                speedStatus = "At Rate";
                statusColor = producingColor;
              } else if (category === SpeedCategory.ABOVE || speedPercent > 0) {
                speedStatus = "Speed Gain";
                statusColor = speedGainDarker;
              } else {
                speedStatus = "Speed Loss";
                statusColor = speedLoss;
              }

              const formattedPercent =
                speedPercent > 0
                  ? `+${speedPercent.toFixed(1)}%`
                  : `${speedPercent.toFixed(1)}%`;

              // Calculate target and actual time between ticks (in seconds)
              // Target time is calculated as 60 seconds divided by target rate (units per minute)
              const targetTimeBetweenTicks =
                targetRate > 0 ? 60 / targetRate : 0;
              const theoreticalTimeBetweenTicks =
                targetTimeBetweenTicks * (100 / (100 + speedPercent));
              const actualTimeBetweenTicks =
                foundSegment?.range && nextFoundSegment?.range
                  ? nextFoundSegment.range.ge - foundSegment.range.ge
                  : theoreticalTimeBetweenTicks;

              // Format values to display in seconds with 1 decimal place
              const formattedTargetTime = targetTimeBetweenTicks.toFixed(1);
              const actualTime = actualTimeBetweenTicks.toFixed(1);
              const formattedActualTime = formatHoursMinutesSeconds(
                Number(actualTime)
              );
              // Add text-shadow for better visibility
              const textShadow =
                statusColor === speedLoss
                  ? `text-shadow: 1px 1px 0px ${speedGainDarker};`
                  : "";

              tooltip.innerHTML = `
              <div class="title" style="color: ${statusColor}; ${textShadow}">${speedStatus}</div>
              <div style="text-align: center;">Speed: <span style="color: ${statusColor}; font-weight: bold; ${textShadow}">${formattedPercent}</span></div>
              <div style="text-align: center;">Target Rate: ${targetRate.toFixed(1)} units/min</div>
              <div style="text-align: center;">Target Time: ${formattedTargetTime} sec</div>
              <div style="text-align: center;">Actual Time: ${formattedActualTime}</div>
              <div>${time}</div>
            `;
            } else {
              // No segment found at current mouse position
              tooltip.innerHTML = `
              <div class="title">Speed Analysis</div>
              <div style="text-align: center;">--</div>
            `;
            }
          } else if (tickType === "speedGainLoss") {
            // Mouse is over future date for speed-gain-loss, show empty state
            tooltip.innerHTML = `
              <div class="title">Speed Analysis</div>
              <div style="text-align: center;">--</div>
            `;
          } else {
            // If no visible ticks or mouse is over future date, show empty state and follow mouse
            if (visibleTicks.length === 0 || isMouseInFuture) {
              tooltip.innerHTML = `
              <div class="title">${TICKS_TITLES[tickType]}</div>
              <div>--</div>
            `;
            } else {
              tooltip.innerHTML = `
              <div class="title">${TICKS_TITLES[tickType]}</div>
              <div>${time}</div>
            `;
            }
          }

          // Use wider tooltip for speed analysis
          const currentTooltipWidth =
            tickType === "speedGainLoss"
              ? SPEED_TOOLTIP_WIDTH
              : BASE_TOOLTIP_WIDTH;

          // Add speed-tooltip class if this is a speed analysis tooltip
          if (tickType === "speedGainLoss") {
            tooltip.classList.add("speed-tooltip");
            if (!closestTick) {
              tooltip.classList.add("empty");
            }
          } else {
            tooltip.classList.remove("speed-tooltip");
            tooltip.classList.remove("empty");
          }

          // Calculate tooltip position - using tooltipPositionX for speed segments
          // Center tooltip on either the segment center (for speed tooltips) or the tick (for others)
          let tooltipPosition =
            (tickType === "speedGainLoss" ? tooltipPositionX : tickX) -
            currentTooltipWidth / 2;

          // For speed gain/loss, adjust tooltip position if we have a next tick
          if (tickType === "speedGainLoss" && closestTick) {
            const nextTick = machemetryTicks[grossOrNetTicks]?.find(
              (tick: any) => tick.ts > closestTick.ts
            );
            const nextTickXPos = nextTick ? scaleTime(nextTick.ts * 1000) : 0;
            if (nextTickXPos) {
              tooltipPosition =
                tickX + (nextTickXPos - tickX) / 2 - currentTooltipWidth / 2;
            }
          }

          // Get the timeline card element and its dimensions
          const timelineCard = canvas.closest(".MuiCard-root");

          // Get the right boundary of the timeline card, or fallback to window width if card not found
          const cardRect = timelineCard?.getBoundingClientRect();
          const cardRightBoundary = cardRect
            ? cardRect.right
            : window.innerWidth;

          // Calculate the right boundary relative to the canvas
          const rightBoundary =
            cardRightBoundary - rect.left - currentTooltipWidth - 12;

          // Keep tooltip within card bounds
          const tooltipLeftPos = clamp(tooltipPosition, -4, rightBoundary);
          const offsetTop =
            safariOffset + windowsOffset + desktopXlOrLargerOffset + macOffset;
          if (visibleTicks.length > 0 && closestTick && !isMouseInFuture) {
            tooltip.style.left = `${tooltipLeftPos}px`;
            tooltip.style.opacity = "1";

            const arrow = document.getElementById("ticks-tooltip-arrow");
            if (arrow) {
              // For speed gain/loss, position arrow directly at the segment center
              const arrowX =
                tickType === "speedGainLoss"
                  ? tooltipPositionX // Use exact center position for speed segments
                  : tickX;
              arrow.style.left = `${arrowX - ARROW_HEIGHT + 1}px`;
              // Adding +1 to match the segment position adjustment in Timeline.tsx
              arrow.style.top = `${32 + 8 + offsetTop + (tickOrder - 1 + 1) * (TAGHIGHLIGHT_BACKDROP_HEIGHT + TAGHIGHLIGHT_BACKDROP_MARGIN_TOP) + ARROW_HEIGHT}px`;
              arrow.classList.remove("hidden");
            }
          } else {
            const mouseX = e.clientX;
            const rectLeft = rect.left;

            // Calculate position based on mouse position
            const emptyStatePosition =
              mouseX - rectLeft - BASE_TOOLTIP_WIDTH / 2;

            // Apply the same clamping as non-empty state to ensure it respects card boundaries
            const clampedPosition = clamp(
              emptyStatePosition,
              -4,
              rightBoundary
            );

            // Set tooltip position using calculated style with boundary clamping
            tooltip.style.left = `${clampedPosition}px`;
            tooltip.style.opacity = "0.6";

            // Hide arrow if no visible tick
            const arrow = document.getElementById("ticks-tooltip-arrow");
            if (arrow) {
              arrow.classList.add("hidden");
            }
          }
          // Adding +1 to match the segment position adjustment in Timeline.tsx
          tooltip.style.top = `${32 + offsetTop + (tickOrder + 1) * (TAGHIGHLIGHT_BACKDROP_HEIGHT + TAGHIGHLIGHT_BACKDROP_MARGIN_TOP)}px`;
          tooltip.classList.remove("hidden");
        }
      };
      const handleMouseLeave = () => {
        const tooltip = document.getElementById("ticks-tooltip");
        if (tooltip) {
          tooltip.classList.add("hidden");
        }
        const arrow = document.getElementById("ticks-tooltip-arrow");
        if (arrow) {
          arrow.classList.add("hidden");
        }
        // When mouse leaves, tooltip is not visible
        setIsTooltipVisible(false);
      };

      // since we need to pass canvas into the event handler, we'll make a separate handler for each canvas
      // to ensure we're removing all event handlers (removing an event handler doesn't work with anonymous functions)
      const handleMouseMove1 = e => handleMouseMove(e, canvases[0]);
      const handleMouseMove2 = e => handleMouseMove(e, canvases[1]);
      const handleMouseMove3 = e => handleMouseMove(e, canvases[2]);
      const handleMouseMove4 = e => handleMouseMove(e, canvases[3]);

      canvases[0].addEventListener("mousemove", handleMouseMove1);
      canvases[1].addEventListener("mousemove", handleMouseMove2);
      canvases[2].addEventListener("mousemove", handleMouseMove3);
      canvases[3]?.addEventListener("mousemove", handleMouseMove4);

      // add mouseleave event listeners to all canvases (no canvas-specific logic needed)
      Array.from(canvases).forEach(canvas => {
        canvas?.addEventListener("mouseleave", handleMouseLeave);
      });
      return () => {
        canvases[0].removeEventListener("mousemove", handleMouseMove1);
        canvases[1].removeEventListener("mousemove", handleMouseMove2);
        canvases[2].removeEventListener("mousemove", handleMouseMove3);
        canvases[3]?.removeEventListener("mousemove", handleMouseMove4);
        Array.from(canvases).forEach(canvas => {
          canvas?.removeEventListener("mouseleave", handleMouseLeave);
        });
      };
    }, [
      // Only include dependencies that should trigger tooltip handler setup
      // Remove xLeft, xRight from dependency array as these change during zooming
      // and we don't need to re-setup event handlers just because visible range changed
      machemetryTicks,
      scaleTime,
      isDesktopXlOrLarger,
      order,
      speedGainLossSegments,
      processId,
      xLeft,
      xRight,
      setIsTooltipVisible,
      latestTicksInfo,
    ]);

    // the returned element is a floating tooltip that will display the timestamp of the tick we're moused over
    return isTooltipVisible ? (
      <foreignObject
        style={{ position: "relative", overflow: "visible", zIndex: 20000 }}
      >
        <StyledTooltipPaper
          id="ticks-tooltip"
          className="hidden"
          elevation={4}
        />
        <StyledArrowDiv
          id="ticks-tooltip-arrow"
          className="hidden"
        ></StyledArrowDiv>
      </foreignObject>
    ) : null;
  }
);
TicksTooltipContent.displayName = "TicksTooltipContent";

const TICKS_ORDER = {
  grossCount_0: 0,
  rejectCount_0: 1,
  netCount_0: 2,
  speedGainLoss: 3,
};

const TICKS_TITLES = {
  grossCount_0: "Gross Count Tick",
  rejectCount_0: "Reject Count Tick",
  netCount_0: "Net Count Tick",
  speedGainLoss: "Speed Analysis",
};
const BASE_TOOLTIP_WIDTH = 126;
const SPEED_TOOLTIP_WIDTH = BASE_TOOLTIP_WIDTH + 48; // wider for speed analysis
const StyledTooltipPaper = styled(Paper)`
  width: ${BASE_TOOLTIP_WIDTH}px;
  &.speed-tooltip:not(.empty) {
    width: ${SPEED_TOOLTIP_WIDTH}px;
  }
  &.hidden {
    display: none;
  }
  box-sizing: border-box;
  position: absolute;
  background-color: white;
  z-index: 20000;
  font-size: 12px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: 1em;
  align-items: center;
  .date {
    color: #666;
  }
`;
const ARROW_HEIGHT = 10;
/** an upward-pointing sharp white arrow triangle that follows the cursor position */
const StyledArrowDiv = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-left: ${ARROW_HEIGHT}px solid transparent;
  border-right: ${ARROW_HEIGHT}px solid transparent;
  border-bottom: ${ARROW_HEIGHT}px solid white;
  z-index: 20001;
  pointer-events: none;
  &.hidden {
    display: none;
  }
  transform: translateY(1px);
`;

function formatHoursMinutesSeconds(durationMs: number) {
  const duration = durationMs / 1000;
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return minutes < 1
    ? `${seconds.toFixed(1)} sec`
    : hours < 1
      ? `${minutes}m ${Math.floor(seconds)}s`
      : `${hours}h ${minutes}m ${Math.floor(seconds)}s`;
}
